import { Link } from "react-router-dom";
import { AlertTriangle, Info, ThermometerSun } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";

const CANONICAL = "https://heilys.info/rotlicht-erkaeltung";
const PUBLISHED = "2026-05-13";
const MODIFIED = "2026-05-13";

type Verdict = "ja" | "bedingt" | "nein";

const SYMPTOMS: { symptom: string; verdict: Verdict; verdictLabel: string; note: string }[] = [
  { symptom: "Schnupfen, verstopfte Nase", verdict: "ja", verdictLabel: "Eher ja", note: "10 Min Anwendung im Gesichtsbereich, 30–40 cm Abstand, 1–2× täglich. Augen geschlossen halten." },
  { symptom: "Druck in den Nebenhöhlen", verdict: "ja", verdictLabel: "Eher ja", note: "Wärme kann das subjektive Druckgefühl lösen. Kein Heilversprechen, ergänzend zu Inhalation und Ruhe." },
  { symptom: "Halsschmerzen, Kratzen", verdict: "ja", verdictLabel: "Eher ja", note: "Hals- und Nackenbereich, ähnlicher Abstand. Bei eitrigem Belag oder hohem Fieber: Arzt." },
  { symptom: "Trockener Reizhusten", verdict: "bedingt", verdictLabel: "Bedingt", note: "Kurze Sitzungen im Brustbereich. Bei produktivem Husten oder Atemnot: Arzt vorrangig." },
  { symptom: "Verspannte Muskulatur durch Krankheitsgefühl", verdict: "ja", verdictLabel: "Eher ja", note: "Nacken und Schultern, normale Wärme-Anwendung wie bei Verspannungen." },
  { symptom: "Fieber über 38,5 °C", verdict: "nein", verdictLabel: "Nein", note: "Zusätzliche Wärmezufuhr vermeiden. Erst Fieber senken, ärztliche Abklärung." },
  { symptom: "Ohrenschmerzen", verdict: "bedingt", verdictLabel: "Vorsicht", note: "Mittelohrentzündungen brauchen ärztliche Abklärung – nicht eigenständig mit Wärme behandeln." },
  { symptom: "Bindehautentzündung, gereizte Augen", verdict: "nein", verdictLabel: "Nein", note: "Augenbereich meiden. Auch Vision-Modus-Geräte sind nicht für entzündete Augen gedacht." },
];

const STEPS: { name: string; text: string }[] = [
  { name: "Position vorbereiten", text: "Stelle die Rotlichtlampe so auf, dass sie 30–40 cm von Gesicht oder Brust entfernt ist. Setz dich entspannt hin, Schultern locker." },
  { name: "Augen schützen", text: "Schließe die Augen während der gesamten Anwendung oder nutze die mitgelieferte Schutzbrille. Auch bei moderner Rot-/NIR-Technik gilt: nicht direkt in die LEDs schauen." },
  { name: "Anwendung starten", text: "Schalte die Lampe ein und atme ruhig durch die Nase. 10 Minuten reichen in der Regel; länger ist nicht automatisch besser." },
  { name: "Nachruhen", text: "Nach der Anwendung 5 Minuten ruhig sitzen oder liegen. Warm halten, nicht direkt in kalte Zugluft gehen." },
  { name: "Frequenz steuern", text: "1–2× täglich für 3–4 Tage testen. Verbessert sich nichts oder werden Symptome stärker, absetzen und ärztlich abklären." },
];

const FAQ: { q: string; a: string }[] = [
  {
    q: "Hilft Rotlicht wirklich gegen Erkältung?",
    a: "Rotlicht heilt keine Erkältung. Was es tatsächlich tut, ist Wärme im behandelten Bereich erzeugen. Diese Wärme wird traditionell als wohltuend bei Schnupfen, Nebenhöhlendruck und Halsschmerzen empfunden. Belastbare klinische Studien speziell zu Erkältungsverläufen sind dünn – die Anwendung ist eher tradiertes Hausmittel als evidenzbasierte Therapie.",
  },
  {
    q: "Wie lange sollte ich Rotlicht bei Erkältung anwenden?",
    a: "Üblich sind 10 Minuten pro Sitzung, 1–2× täglich, 30–40 cm Abstand. Mehr Wärme bringt nicht mehr Effekt, aber mehr Belastung für Haut und Kreislauf. Wenn nach 3–4 Tagen keine subjektive Besserung eintritt, ist die Methode für dich vermutlich nicht der richtige Weg.",
  },
  {
    q: "Welcher Abstand ist sinnvoll?",
    a: "30–40 cm zur klassischen Infrarotlampe. Es soll spürbar warm sein, aber nicht unangenehm heiß. Bei modernen Rot-/NIR-Panels orientierst du dich an der Herstellerangabe – die liegt bei kompakten Geräten oft enger (15–25 cm), weil die Lichtleistung pro Fläche niedriger ist als bei einer reinen Wärmelampe.",
  },
  {
    q: "Darf ich Rotlicht bei Fieber nutzen?",
    a: "Nein. Bei Fieber ab etwa 38,5 °C solltest du keine zusätzliche Wärme von außen zuführen. Der Körper arbeitet bereits an einer Immunreaktion, weitere Wärme kann den Kreislauf belasten. Erst Fieber senken, dann gegebenenfalls ärztlich abklären lassen.",
  },
  {
    q: "Rotlicht oder Inhalation – was ist besser bei Schnupfen?",
    a: "Beides hat einen anderen Wirkpfad. Inhalation befeuchtet direkt die Schleimhäute und kann zähen Schleim lösen. Rotlicht wirkt von außen über Wärme, vor allem auf Stirn- und Kieferhöhlen. Viele kombinieren beides. Wenn du dich nur für eines entscheiden musst und stark verschleimt bist, ist Inhalation in der Regel der wirksamere Hebel.",
  },
  {
    q: "Klassische Infrarotlampe oder modernes Rot-/NIR-Panel?",
    a: "Eine klassische IR-Lampe (z. B. Beurer, Philips InfraCare) liefert vor allem Wärme. Ein modernes Panel mit Rot- und Nahinfrarot-LEDs liefert deutlich weniger Wärme, dafür spezifischere Wellenlängen. Für die akute Erkältungssitzung ist die klassische Wärmelampe einfacher; ein Mehrwellenlängen-Gerät spielt seine Stärken eher in regelmäßigen Routinen aus.",
  },
  {
    q: "Wie oft pro Tag ist Rotlicht okay?",
    a: "Bei akuter Erkältung 1–2× täglich, jeweils rund 10 Minuten. Mehrere Sitzungen am Tag sind nicht nötig und können die Haut austrocknen. Trinke nach der Anwendung ausreichend Wasser.",
  },
  {
    q: "Hilft Rotlicht bei Nebenhöhlen und verstopfter Nase?",
    a: "Bei Druck in den Nebenhöhlen und verstopfter Nase wird Rotlicht traditionell als wohltuend empfunden. Die Wärme erreicht über Stirn und Kieferbereich subjektiv das Druckgefühl und kann das Atmen kurzzeitig erleichtern. Eine Behandlung von Nasennebenhöhlenentzündungen ersetzt es nicht – bei eitrigem Sekret, Fieber oder Beschwerden länger als eine Woche gehört der Weg zum Arzt vor jede Selbstanwendung. Für die Nase selbst gilt: 30–40 cm Abstand, Augen geschlossen, 10 Minuten reichen.",
  },
  {
    q: "Wie lange Rotlicht ist sinnvoll?",
    a: "Pro Sitzung sind 10 Minuten der bewährte Richtwert, 1–2× täglich. Mehr Zeit oder kürzerer Abstand bringen keinen zusätzlichen Effekt, belasten aber Haut und Kreislauf. Wer länger als 3–4 Tage keine subjektive Besserung spürt, sollte absetzen und ärztlich abklären lassen – Rotlicht ist ein Hausmittel, kein Therapeutikum.",
  },
  {
    q: "Ist Rotlicht für Kinder geeignet?",
    a: "Nur unter Aufsicht und mit größerem Abstand. Kinderhaut reagiert empfindlicher auf Wärme. Bei Säuglingen und Kleinkindern grundsätzlich erst mit dem Kinderarzt sprechen. Augenbereich konsequent meiden.",
  },
];

const verdictTone: Record<Verdict, string> = {
  ja: "bg-success/15 text-success border-success/30",
  bedingt: "bg-warning/15 text-warning border-warning/30",
  nein: "bg-destructive/15 text-destructive border-destructive/30",
};

const RotlichtErkaeltung = () => {
  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Startseite", item: "https://heilys.info/" },
      { "@type": "ListItem", position: 2, name: "Rotlicht bei Erkältung", item: CANONICAL },
    ],
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const howToSchema = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "Rotlicht bei Erkältung richtig anwenden",
    description: "Schritt-für-Schritt-Anleitung für die Rotlichtanwendung bei Schnupfen, Nebenhöhlendruck und Halsschmerzen.",
    totalTime: "PT10M",
    tool: [{ "@type": "HowToTool", name: "Rotlichtlampe oder Rot-/NIR-Panel" }],
    supply: [
      { "@type": "HowToSupply", name: "Handtuch" },
      { "@type": "HowToSupply", name: "Wasser zum Trinken nach der Anwendung" },
    ],
    step: STEPS.map((s, i) => ({
      "@type": "HowToStep",
      position: i + 1,
      name: s.name,
      text: s.text,
    })),
  };

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Rotlicht bei Erkältung: Wann es hilft – und wann nicht",
    description: "Wirkung, Anwendung, Symptom-Tabelle und klare Grenzen der Rotlichttherapie bei Erkältung.",
    mainEntityOfPage: CANONICAL,
    datePublished: PUBLISHED,
    dateModified: MODIFIED,
    author: { "@type": "Person", name: "Bene", url: "https://heilys.info/" },
    publisher: { "@type": "Organization", name: "heilys.info", url: "https://heilys.info/" },
  };

  return (
    <Layout>
      <SEOHead
        title="Rotlicht bei Erkältung: Anwendung, Dauer & Code LONGEVITY"
        description="Rotlicht bei Erkältung: Wirkung, Anwendung & Dauer. Symptom-Tabelle, Grenzen, ohne Heilversprechen. Plus 5 % Rabatt mit Code LONGEVITY."
        canonical={CANONICAL}
        ogType="article"
        ogImageAlt="Rotlicht bei Erkältung – Anwendung, Symptom-Tabelle und Grenzen"
        schemaJson={[breadcrumb, articleSchema, howToSchema, faqSchema]}
      />

      <article className="container py-12 md:py-20">
        <div className="mx-auto max-w-3xl">
          <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
            <Link to="/" className="hover:text-foreground">Startseite</Link>
            <span className="mx-2">›</span>
            <span className="text-foreground">Rotlicht bei Erkältung</span>
          </nav>

          <h1 className="mt-4 font-display text-3xl font-bold text-foreground sm:text-4xl">
            Rotlicht bei Erkältung – was sinnvoll ist und was nicht
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground sm:text-base">
            Rotlicht ist ein klassisches Hausmittel bei Schnupfen, Nebenhöhlendruck und Halsschmerzen.
            Die Erwartungen sind oft zu hoch, die Wirkmechanismen unspektakulärer als gedacht. Diese
            Seite ordnet ehrlich ein, wann Rotlicht bei einer Erkältung sinnvoll sein kann, wann nicht
            – und wie du es so anwendest, dass es dir tatsächlich guttut.
          </p>

          {/* Top-Disclaimer */}
          <div className="mt-6 flex items-start gap-3 rounded-2xl border border-warning/30 bg-warning/10 p-4">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-warning" aria-hidden="true" />
            <p className="text-sm leading-relaxed text-foreground">
              <span className="font-semibold">Kein Heilversprechen.</span> Rotlicht ersetzt keine
              ärztliche Behandlung. Bei Fieber über 38,5 °C, starken Schmerzen, Atemnot oder
              Symptomen, die länger als eine Woche anhalten, gehört der Weg zum Arzt vor jede
              Selbstanwendung.
            </p>
          </div>

          {/* TL;DR */}
          <section className="mt-10">
            <h2 className="font-display text-xl font-bold text-foreground sm:text-2xl">Das Wichtigste in 30 Sekunden</h2>
            <ul className="mt-4 space-y-2 rounded-2xl border border-border bg-card p-5 text-sm leading-relaxed text-foreground sm:text-base">
              <li>• Rotlicht wirkt vor allem über <strong>Wärme</strong> – kein Antibiotikum, keine Virenkur.</li>
              <li>• Subjektiv hilfreich bei Schnupfen, Nebenhöhlendruck und Halskratzen.</li>
              <li>• <strong>Nicht</strong> anwenden bei Fieber, akuter Mittelohrentzündung oder entzündeten Augen.</li>
              <li>• Standardsetting: 10 Minuten, 30–40 cm Abstand, 1–2× täglich, max. 3–4 Tage testen.</li>
              <li>• Inhalation, Ruhe, ausreichend trinken bleiben die wichtigeren Säulen einer Erkältung.</li>
            </ul>
          </section>

          {/* Wirkmechanismus */}
          <section className="mt-12">
            <h2 className="font-display text-xl font-bold text-foreground sm:text-2xl">Was Rotlicht bei Erkältung tatsächlich macht</h2>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
              Klassische Rotlichtlampen (z. B. mit Infrarot-A-Strahler) erzeugen vor allem Wärme im
              behandelten Hautareal. Diese Wärme regt die lokale Durchblutung an und wird subjektiv
              als angenehm empfunden – ein Effekt, den jeder kennt, der schon mal mit warmen Wickeln
              gegen einen verstopften Kopf gearbeitet hat. Eine Erkältung selbst – ausgelöst meist
              durch Rhino- oder Coronaviren – heilt das nicht.
            </p>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
              Moderne Rot- und Nahinfrarot-Panels (Photobiomodulation) arbeiten mit deutlich weniger
              Wärme, dafür mit definierten Wellenlängen wie 660 nm oder 850 nm. Die Studienlage zu
              Atemwegsinfekten ist dort noch limitierter; die meisten Forschungen drehen sich um
              Hautregeneration, Muskelregeneration und Augenanwendungen, nicht um Schnupfen.
              Für die akute Erkältung ist die klassische Wärmelampe das robustere Hausmittel.
            </p>
          </section>

          {/* Symptom-Tabelle */}
          <section className="mt-12">
            <h2 className="font-display text-xl font-bold text-foreground sm:text-2xl">Symptom-Tabelle: Wann Rotlicht sinnvoll ist</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Orientierung, keine medizinische Empfehlung. Im Zweifel oder bei Vorerkrankungen mit
              Hausarzt oder Apotheke sprechen.
            </p>

            {/* Desktop: Tabelle */}
            <div className="mt-4 hidden overflow-hidden rounded-xl border border-border bg-card sm:block">
              <table className="w-full text-sm">
                <caption className="sr-only">Übersicht typischer Erkältungssymptome und ob Rotlicht hilfreich sein kann</caption>
                <thead className="bg-secondary/40 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th scope="col" className="px-5 py-3">Symptom</th>
                    <th scope="col" className="px-5 py-3">Sinnvoll?</th>
                    <th scope="col" className="px-5 py-3">Hinweis</th>
                  </tr>
                </thead>
                <tbody>
                  {SYMPTOMS.map((s) => (
                    <tr key={s.symptom} className="border-t border-border/60 align-top">
                      <th scope="row" className="px-5 py-3 text-left font-medium text-foreground">{s.symptom}</th>
                      <td className="px-5 py-3">
                        <span className={`inline-flex rounded-md border px-2 py-0.5 text-xs font-semibold ${verdictTone[s.verdict]}`}>
                          {s.verdictLabel}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-muted-foreground">{s.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile: Cards */}
            <ul className="mt-4 space-y-3 sm:hidden">
              {SYMPTOMS.map((s) => (
                <li key={s.symptom} className="rounded-xl border border-border bg-card p-4">
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-display text-sm font-semibold text-foreground">{s.symptom}</h3>
                    <span className={`shrink-0 rounded-md border px-2 py-0.5 text-xs font-semibold ${verdictTone[s.verdict]}`}>
                      {s.verdictLabel}
                    </span>
                  </div>
                  <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{s.note}</p>
                </li>
              ))}
            </ul>
          </section>

          {/* HowTo */}
          <section className="mt-12">
            <h2 className="font-display text-xl font-bold text-foreground sm:text-2xl">Anwendung Schritt für Schritt</h2>
            <ol className="mt-4 space-y-3">
              {STEPS.map((s, i) => (
                <li key={s.name} className="flex gap-4 rounded-xl border border-border bg-card p-4">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 font-display text-sm font-bold text-primary">
                    {i + 1}
                  </span>
                  <div>
                    <h3 className="font-display text-sm font-semibold text-foreground">{s.name}</h3>
                    <p className="mt-1 text-xs leading-relaxed text-muted-foreground sm:text-sm">{s.text}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          {/* Kontraindikationen */}
          <section className="mt-12">
            <h2 className="font-display text-xl font-bold text-foreground sm:text-2xl">Wann du besser nicht zur Rotlichtlampe greifst</h2>
            <ul className="mt-4 space-y-2 rounded-2xl border border-destructive/30 bg-destructive/5 p-5 text-sm leading-relaxed text-foreground">
              <li className="flex gap-2"><ThermometerSun className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden="true" /> <span><strong>Fieber über 38,5 °C</strong> – keine zusätzliche Wärme von außen.</span></li>
              <li className="flex gap-2"><ThermometerSun className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden="true" /> <span><strong>Akute Entzündungen</strong> wie Mittelohrentzündung, Mandelabszess, Lungenentzündung – ärztliche Abklärung statt Selbstwärme.</span></li>
              <li className="flex gap-2"><ThermometerSun className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden="true" /> <span><strong>Schwerer Bluthochdruck oder Herz-Kreislauf-Vorerkrankungen</strong> – Wärme belastet den Kreislauf.</span></li>
              <li className="flex gap-2"><ThermometerSun className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden="true" /> <span><strong>Schwangerschaft</strong> – im Zweifel mit Hebamme oder Ärztin abstimmen.</span></li>
              <li className="flex gap-2"><ThermometerSun className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden="true" /> <span><strong>Augenerkrankungen</strong> wie Bindehautentzündung – Augenbereich konsequent meiden.</span></li>
              <li className="flex gap-2"><ThermometerSun className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden="true" /> <span><strong>Kleinkinder und Säuglinge</strong> – nur unter Aufsicht und nach Rücksprache mit dem Kinderarzt.</span></li>
            </ul>
          </section>

          {/* Hausmittel-Vergleich */}
          <section className="mt-12">
            <h2 className="font-display text-xl font-bold text-foreground sm:text-2xl">Rotlicht im Vergleich zu klassischen Hausmitteln</h2>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
              Rotlicht ist eine Säule unter mehreren – und oft nicht die wichtigste. Eine ehrliche Einordnung:
            </p>
            <ul className="mt-4 space-y-2 text-sm leading-relaxed text-foreground">
              <li>• <strong>Inhalation</strong> mit Wasserdampf befeuchtet die Schleimhäute direkt – meist wirksamer gegen verstopfte Nase als Wärme von außen.</li>
              <li>• <strong>Ausreichend trinken</strong> hält den Schleim flüssig – simpel, aber unterschätzt.</li>
              <li>• <strong>Ruhe und Schlaf</strong> sind die Bedingung dafür, dass das Immunsystem aufräumt – jede Maßnahme, die das fördert (auch Rotlicht-Wärme zum Entspannen), ist wertvoll.</li>
              <li>• <strong>Zink</strong> kann den Verlauf laut Studienlage moderat verkürzen, sofern früh begonnen.</li>
              <li>• <strong>Rotlicht</strong> ergänzt diese Basis – als Wohlfühlfaktor und gezielte Wärme, nicht als Ersatz.</li>
            </ul>
          </section>

          {/* Geräteklassen */}
          <section className="mt-12">
            <h2 className="font-display text-xl font-bold text-foreground sm:text-2xl">Welches Gerät passt für die Erkältungsanwendung?</h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div className="rounded-xl border border-border bg-card p-5">
                <h3 className="font-display text-sm font-semibold text-foreground">Klassische Infrarotlampe</h3>
                <p className="mt-2 text-xs leading-relaxed text-muted-foreground sm:text-sm">
                  Erzeugt vor allem Wärme. Robust, günstig, einfach zu bedienen. Genau richtig, wenn
                  es im Krankheitsfall vor allem um wohltuende Wärme geht. Beispiele: Beurer IL 50,
                  Philips InfraCare. Diese Geräte verkaufen wir nicht – sie sind im stationären
                  Handel und bei Apotheken erhältlich.
                </p>
              </div>
              <div className="rounded-xl border border-border bg-card p-5">
                <h3 className="font-display text-sm font-semibold text-foreground">Modernes Rot-/NIR-Panel</h3>
                <p className="mt-2 text-xs leading-relaxed text-muted-foreground sm:text-sm">
                  Mehrere Wellenlängen (z. B. 660 nm Rot und 850 nm NIR), weniger Wärme, dafür
                  Photobiomodulation. Für regelmäßige Routinen interessanter als für die einzelne
                  Erkältung. Wenn dich die Technik dahinter interessiert: Wir haben das{" "}
                  <Link to="/gen30-vision" className="text-primary underline-offset-4 hover:underline">
                    heilys Gen30+ Vision
                  </Link>{" "}
                  als Beispielgerät genauer eingeordnet.
                </p>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="mt-12">
            <h2 className="font-display text-xl font-bold text-foreground sm:text-2xl">Häufige Fragen</h2>
            <div className="mt-4 space-y-3">
              {FAQ.map((f) => (
                <details key={f.q} className="group rounded-xl border border-border bg-card p-4 open:bg-card">
                  <summary className="cursor-pointer list-none font-display text-sm font-semibold text-foreground marker:hidden">
                    {f.q}
                  </summary>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
                </details>
              ))}
            </div>
          </section>

          {/* Disclaimer */}
          <section className="mt-12 rounded-2xl border border-border bg-card p-5">
            <div className="flex items-start gap-3">
              <Info className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" aria-hidden="true" />
              <div>
                <h2 className="font-display text-base font-semibold text-foreground">Hinweis & Quellen</h2>
                <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                  Die Inhalte auf dieser Seite dienen ausschließlich der allgemeinen Information und
                  ersetzen keine medizinische Beratung. Belastbare klinische Studien speziell zur
                  Anwendung von Rotlicht bei Erkältung sind begrenzt – die hier formulierten
                  Empfehlungen orientieren sich an etablierter Anwendungspraxis und allgemein
                  gängigen Sicherheitshinweisen (u. a. RKI-Empfehlungen zu Fiebermanagement,
                  pharmazeutische Beratungspraxis bei akuten Atemwegsinfekten). Bei anhaltenden
                  oder starken Beschwerden ist die ärztliche Abklärung Pflicht.
                </p>
                <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
                  Mehr zur Unabhängigkeit dieser Seite findest du im{" "}
                  <Link to="/transparenz" className="text-primary underline-offset-4 hover:underline">
                    Transparenzhinweis
                  </Link>
                  .
                </p>
              </div>
            </div>
          </section>
        </div>
      </article>
    </Layout>
  );
};

export default RotlichtErkaeltung;
